import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowLeft, Shield, Heart, Lock, Mail, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // If already logged in, redirect to the appropriate portal
  useEffect(() => {
    if (user) {
      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/portal', { replace: true });
      }
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loggedUser = await login(email.trim(), password);
      
      if (loggedUser.role === 'admin') {
        toast.success('Welcome to Charitage Admin Control Panel!');
        navigate('/admin');
      } else {
        toast.success(`Welcome back, ${loggedUser.name || 'Donor'}!`);
        navigate('/portal');
      }
    } catch (error) {
      console.error('Login failed:', error);
      const msg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Invalid credentials. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-primary to-slate-950 flex flex-col justify-between p-4 sm:p-6 lg:p-8 font-body relative overflow-hidden">
      {/* Background Decorative Accents */}
      <div className="absolute top-0 right-0 -mr-24 -mt-24 w-96 h-96 rounded-full bg-secondary/15 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -ml-24 -mb-24 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />

      {/* Top Header Bar */}
      <header className="w-full max-w-6xl mx-auto flex items-center justify-between z-10 py-2">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-semibold transition-colors bg-white/10 hover:bg-white/15 px-4 py-2 rounded-full backdrop-blur-sm border border-white/10"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Main Website</span>
        </Link>

        <div className="hidden sm:flex items-center gap-2 text-xs text-white/70 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
          <Shield className="w-3.5 h-3.5 text-secondary" />
          <span>256-Bit SSL Secure Portal</span>
        </div>
      </header>

      {/* Main Login Card */}
      <main className="w-full max-w-md mx-auto my-auto z-10 py-6">
        <div className="bg-white rounded-3xl shadow-2xl border border-slate-100 p-8 sm:p-10 relative">
          {/* Logo & Heading */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-4">
              <img
                src="/logo-landscape.png"
                alt="Charitage Foundation"
                className="h-12 mx-auto object-contain"
              />
            </Link>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold text-slate-900 tracking-tight">
              Portal Sign In
            </h1>
            <p className="text-slate-500 text-sm mt-2">
              Sign in to manage your donations, tax receipts & activities
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="login-email" className="text-xs font-bold uppercase tracking-wider text-slate-700 block mb-2">
                Email Address
              </Label>
              <div className="relative">
                <Input
                  id="login-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  className="pl-11 h-12 rounded-xl border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-900 text-sm"
                />
                <Mail className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="login-password" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Password
                </Label>
                <Link
                  to="/register?tab=donor"
                  className="text-xs font-semibold text-secondary hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-11 pr-11 h-12 rounded-xl border-slate-200 focus:border-primary focus:ring-primary/20 text-slate-900 text-sm"
                />
                <Lock className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="p-1.5 text-slate-400 hover:text-slate-700 absolute right-3 top-1/2 -translate-y-1/2 rounded-lg"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-primary hover:bg-primary/95 text-white font-bold text-base rounded-xl shadow-lg shadow-primary/20 transition-all transform hover:scale-[1.01] active:scale-[0.99] mt-2"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Signing In...</span>
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Divider & Register Link */}
          <div className="mt-8 pt-6 border-t border-slate-100 text-center">
            <p className="text-sm text-slate-600">
              Don't have a donor account yet?{' '}
              <Link
                to="/register"
                className="font-bold text-secondary hover:text-secondary/80 transition-colors"
              >
                Join & Register
              </Link>
            </p>
          </div>

          {/* Role Access Indicator */}
          <div className="mt-6 bg-slate-50 rounded-xl p-3 border border-slate-100 flex items-center gap-3 text-left">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-slate-600">
              Universal Login: Both <span className="font-semibold text-slate-900">Donors</span> and <span className="font-semibold text-slate-900">System Admins</span> are automatically directed to their respective dashboards.
            </p>
          </div>
        </div>
      </main>

      {/* Footer Note */}
      <footer className="w-full max-w-6xl mx-auto text-center z-10 py-2">
        <p className="text-xs text-white/50">
          © {new Date().getFullYear()} Charitage Foundation. All rights reserved. Leading with Kindness.
        </p>
      </footer>
    </div>
  );
};

export default LoginPage;
